// Shared phone-number formatting + validation, used anywhere we turn a
// country + a raw typed number into the E.164 contact we actually message
// (Start a Session's partner phone field, the notify-me phone prompt, etc).
//
// Why this exists: naively concatenating the selected country's dial code
// in front of whatever was typed (`+233` + `233551059820`) silently produces
// a broken number the moment someone re-types the code themselves, or leaves
// a leading trunk "0" in a local number. That broken number then only fails
// once WhatsApp tries to deliver it, days later, with no obvious cause. This
// module uses real phone-numbering-plan rules instead: it recognises and
// strips a re-typed country code or a leading trunk digit, and reports
// whether what's left is an actual, deliverable number for that country.
import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';

export interface PhoneCheckResult {
  /** Corrected, deliverable number in +<countrycode><number> form, or null if it couldn't be made into one. */
  e164: string | null;
  valid: boolean;
}

export function formatPhoneNumber(rawNumber: string, iso: string): PhoneCheckResult {
  const trimmed = (rawNumber ?? '').trim();
  if (!trimmed) return { e164: null, valid: false };
  try {
    const parsed = parsePhoneNumberFromString(trimmed, iso as CountryCode);
    if (parsed && parsed.isValid()) return { e164: parsed.number, valid: true };
  } catch {
    // Malformed input (e.g. an iso libphonenumber doesn't recognise) — fall through to invalid.
  }
  return { e164: null, valid: false };
}
